import React, {forwardRef, useImperativeHandle} from 'react';
import './Stack.scss';
import {StackPage, StackApi, StackPageDisplayState} from './Stack.types';
import {defer, delay} from '../../utils/Timing';

interface StackPageEx
    extends StackPage {
  displayState: StackPageDisplayState;
}

/**
 * A Stack component that manages a stack of pages, allowing for push and pop operations.
 */
const Stack = forwardRef<StackApi, {}>((_, ref) => {

  const [pages, setPages] = React.useState<(StackPageEx)[]>([]);

  useImperativeHandle(ref, () => ({
    replace: (page: StackPage) => {
      replaceStackPage(page);
    },
    push:    (page: StackPage) => {
      pushStackPage(page);
    },
    pop:     () => {
      popStackPage();
    }
  }));

  /**
   * Pushes a new page onto the stack. If a page with the same ID already exists, it will be replaced.
   *
   * @param {StackPage} page Page to push onto the stack.
   */
  function pushStackPage(page: StackPage) {
    setPages((prev) => {
      const pageEx = {
        ...page,
        displayState: StackPageDisplayState.HIDDEN // Hidden by default, will be set to visible during animation
      };

      let didReplacePage = false;
      const pagesWithReplacedPage = prev.map((existingPage) => {
        if (existingPage.id === page.id) {
          didReplacePage = true;
          return pageEx;
        } else {
          return existingPage;
        }
      });

      const updatedPages = didReplacePage ? pagesWithReplacedPage : [...prev, pageEx];

      if (updatedPages.length > 1) {
        defer(() => animateInPage());
        delay(500, () => animationComplete());
      } else {
        defer(() => animationComplete());
      }

      return updatedPages;
    });
  }

  /**
   * Pops the top page from the stack. If there is only one page, it will not be removed.
   */
  function popStackPage() {
    setPages((prev) => {
      if (prev.length <= 1) {
        return prev;
      }

      const updatedPages = [...prev];
      updatedPages.forEach(page => page.displayState = StackPageDisplayState.HIDDEN);
      updatedPages.at(-1)!.displayState = StackPageDisplayState.ANIMATE_OUT_TO_RIGHT;
      updatedPages.at(-2)!.displayState = StackPageDisplayState.ANIMATE_IN_FROM_LEFT;

      delay(500, () => {
        setPages(prev => {
          const pagesAfterAnimation = prev.slice(0, -1);
          pagesAfterAnimation.forEach(page => page.displayState = StackPageDisplayState.HIDDEN);
          pagesAfterAnimation.at(-1)!.displayState = StackPageDisplayState.VISIBLE;
          return pagesAfterAnimation;
        });
      });

      return updatedPages;
    });
  }

  function replaceStackPage(page: StackPage) {
    setPages((prev) => {
      const pageEx = {
        ...page,
        displayState: StackPageDisplayState.VISIBLE
      };

      return prev.map((existingPage) => {
        if (existingPage.id === page.id) {
          return pageEx;
        } else {
          return existingPage;
        }
      });
    });
  }

  function animateInPage() {
    setPages(prev => {
      const updatedPagesWithAnimations = prev.map(each => ({
        ...each
      }));
      updatedPagesWithAnimations.at(-1)!.displayState = StackPageDisplayState.ANIMATE_IN_FROM_RIGHT;
      updatedPagesWithAnimations.at(-2)!.displayState = StackPageDisplayState.ANIMATE_OUT_TO_LEFT;
      return updatedPagesWithAnimations;
    });
  }

  function animationComplete() {
    setPages(prev => prev.map((p, idx, arr) => ({
      ...p,
      displayState: idx === arr.length - 1 ? StackPageDisplayState.VISIBLE : StackPageDisplayState.HIDDEN
    })));
  }

  /**
   * Renders a stack page.
   *
   * @param {StackPage} page The stack page to render.
   * @return {React.ReactNode} The rendered page.
   */
  function renderStackPage(page: StackPageEx): React.ReactNode {

    const displayStateCssClasses = {
      [StackPageDisplayState.VISIBLE]:               '',
      [StackPageDisplayState.HIDDEN]:                'hidden',
      [StackPageDisplayState.ANIMATE_IN_FROM_RIGHT]: 'animate-in-from-right offscreen-right',
      [StackPageDisplayState.ANIMATE_IN_FROM_LEFT]:  'animate-in-from-left',
      [StackPageDisplayState.ANIMATE_OUT_TO_RIGHT]:  'animate-out-to-right',
      [StackPageDisplayState.ANIMATE_OUT_TO_LEFT]:   'animate-out-to-left'
    };
    const cssClasses = ['stack-page', displayStateCssClasses[page.displayState]].join(' ').trim();

    return <div className={cssClasses}
                key={page.id}>
      {page.nodeProvider()}
    </div>;
  }

  return (
      <div className="stack">
        {pages.map(page => renderStackPage(page))}
      </div>
  );
});

export default Stack;
